import {
  AuthoringContext,
  resolveGraphFrontendComponentAllowlist,
  type ActionType,
  type NodeActionGroup,
} from '@super/shared-types'

/** Action types the picker + validator both treat as supported on a node. */
const ALLOWED_ACTION_TYPES = new Set<ActionType>(
  resolveGraphFrontendComponentAllowlist(
    AuthoringContext.SUPER_ADMIN,
  ).allowedActionTypes,
)

interface ActionStep {
  id: string
  type: string
  nodeActionGroupId?: string
}

interface CanvasStateLike {
  steps: Array<{ id: string; type: string } & Record<string, unknown>>
}

/**
 * Walks every action node on the canvas and returns the id of the first one
 * whose referenced NodeActionGroup fails the same per-row checks the inline
 * action form runs on save: EMAIL/SMS/PHONE_CALL need a non-empty recipient,
 * PUSH_INTEGRATION needs an action subtype. An action node with no attached
 * group counts as invalid.
 *
 * Pure — exported for testing against hand-crafted group fixtures.
 */
export function findInvalidActionNode(
  state: CanvasStateLike,
  groupsById: Record<string, NodeActionGroup | null | undefined>,
): string | null {
  for (const rawStep of state.steps) {
    const step = rawStep as ActionStep
    if (step.type !== 'action') continue
    const groupId = step.nodeActionGroupId
    if (!groupId) return step.id
    const group = groupsById[groupId]
    if (!group) continue
    if (group.members.length === 0) return step.id
    for (const member of group.members) {
      if (!isMemberValid(member)) return step.id
    }
  }
  return null
}

function isMemberValid(member: NodeActionGroup['members'][number]): boolean {
  const { actionType, actionSubtype, parameters } = member.workflowAction
  // Catalog group members outside the picker allowlist land via direct
  // attachment or imported data. The runtime dispatcher skips them; the
  // validator surfaces that at save time so the author sees the issue.
  if (!ALLOWED_ACTION_TYPES.has(actionType as ActionType)) return false
  const params = (parameters ?? {}) as Record<string, unknown>
  switch (actionType) {
    case 'EMAIL':
      return nonEmptyString(params.email)
    case 'SMS':
    case 'PHONE_CALL':
      return nonEmptyString(params.number)
    case 'PUSH_INTEGRATION':
      return actionSubtype != null
    default:
      return true
  }
}

function nonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0
}
